import { useState } from "react";
import { useArenaStore } from "../../stores/useArenaStore";
import { sendUpgrade } from "../../lib/network";
import {
  ARROW_ORDER,
  ARROW_TYPES,
  UPGRADES,
  UPGRADE_ORDER,
  upgradeCost,
} from "@shared/constants";
import type { ArenaState, ArrowType, UpgradeId } from "@shared/types";

interface UpgradeShopProps {
  state: ArenaState;
}

export function UpgradeShop({ state }: UpgradeShopProps) {
  const { playerId, arenaId, setShopOpen } = useArenaStore();
  const [error, setError] = useState<string | null>(null);
  const me = state.players.find((p) => p.id === playerId);
  if (!me) return null;

  const fame = me.kills * 2 + me.level * 3;

  const buy = async (upgrade: UpgradeId, arrowType?: string) => {
    if (!arenaId || !playerId) return;
    setError(null);
    try {
      const res = await sendUpgrade(arenaId, playerId, upgrade, arrowType);
      if (!res.success && res.error) setError(res.error);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed");
    }
  };

  return (
    <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="max-h-[85vh] w-full max-w-3xl overflow-y-auto rounded-2xl border border-archer-700 bg-archer-900 p-6 shadow-2xl">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-black text-white">🔮 Upgrade Shop</h2>
            <p className="text-sm text-archer-300">
              Spend <span className="font-bold text-dusk-400">{fame} fame</span> (kills × 2 + level × 3)
            </p>
          </div>
          <button
            onClick={() => setShopOpen(false)}
            className="rounded-lg bg-archer-700 px-4 py-2 font-medium hover:bg-archer-500"
          >
            Close (E)
          </button>
        </div>

        {error && (
          <div className="mb-4 rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-2 text-sm text-red-400">
            {error}
          </div>
        )}

        {/* Arrow types */}
        <div className="mb-6">
          <h3 className="mb-2 text-sm font-bold uppercase tracking-wide text-archer-300">
            Arrow Craft
          </h3>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {ARROW_ORDER.map((type: ArrowType) => {
              const spec = ARROW_TYPES[type];
              const unlocked = me.level >= spec.unlockLevel;
              const active = me.arrowType === type;
              return (
                <button
                  key={type}
                  disabled={!unlocked || active}
                  onClick={() => buy("arrow_type", type)}
                  className={`rounded-xl border-2 p-3 text-left transition-all ${
                    active
                      ? "border-white bg-white/10"
                      : unlocked
                        ? "border-archer-700 bg-archer-950 hover:border-archer-500"
                        : "cursor-not-allowed border-archer-800 bg-archer-950/50 opacity-50"
                  }`}
                >
                  <div
                    className="mb-1 inline-block h-3 w-3 rounded-full"
                    style={{ background: `#${spec.color.toString(16).padStart(6, "0")}` }}
                  />
                  <div className="font-bold text-white">{spec.name}</div>
                  <div className="text-xs text-archer-300">
                    {spec.damageMult}× dmg
                  </div>
                  {!unlocked && (
                    <div className="mt-1 text-[10px] text-red-400">
                      Requires Lv.{spec.unlockLevel}
                    </div>
                  )}
                  {active && (
                    <div className="mt-1 text-[10px] text-green-400">Equipped</div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Stat upgrades */}
        <h3 className="mb-2 text-sm font-bold uppercase tracking-wide text-archer-300">
          Enhancements
        </h3>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {UPGRADE_ORDER.filter((u) => u !== "arrow_type").map((id: UpgradeId) => {
            const spec = UPGRADES[id];
            const level = me.upgrades[id] ?? 0;
            const maxed = level >= spec.maxLevel;
            const cost = upgradeCost(spec, level);
            const affordable = fame >= cost;
            return (
              <div
                key={id}
                className="flex items-center justify-between rounded-xl border border-archer-700 bg-archer-950 p-3"
              >
                <div>
                  <div className="font-bold text-white">
                    {spec.icon} {spec.name}
                    <span className="ml-2 text-xs text-archer-500">
                      {level}/{spec.maxLevel}
                    </span>
                  </div>
                  <div className="text-xs text-archer-300">{spec.description}</div>
                  {/* level pips */}
                  <div className="mt-1 flex gap-0.5">
                    {Array.from({ length: spec.maxLevel }).map((_, i) => (
                      <div
                        key={i}
                        className={`h-1 w-4 rounded-full ${
                          i < level ? "bg-dawn-500" : "bg-archer-800"
                        }`}
                      />
                    ))}
                  </div>
                </div>
                <button
                  disabled={maxed || !affordable}
                  onClick={() => buy(id)}
                  className={`ml-3 shrink-0 rounded-lg px-3 py-2 text-sm font-bold transition-all ${
                    maxed
                      ? "cursor-not-allowed bg-archer-800 text-archer-500"
                      : affordable
                        ? "bg-gradient-to-r from-dawn-600 to-dawn-500 text-white hover:brightness-110"
                        : "cursor-not-allowed bg-archer-800 text-archer-500"
                  }`}
                >
                  {maxed ? "MAX" : `${cost} ✦`}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
